import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, MoreVertical, Archive, Lock, Unlock, Copy, Printer, FileText } from 'lucide-react';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { formatDate } from '@/lib/utils';
import type { Order } from './types';

interface OrderHeaderProps {
  order: Order;
  onCancel: () => void;
  onArchive: () => void;
  onLock: () => void;
  onDuplicate: () => void;
  onBack: () => void;
}

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const getStatusBadgeVariant = (status: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
  switch (status) {
    case 'pending': return 'yellow';
    case 'processing': case 'shipped': return 'blue';
    case 'delivered': return 'green';
    case 'cancelled': case 'refunded': return 'red';
    default: return 'gray';
  }
};

const paymentBadgeVariant = (ps: string): 'green' | 'yellow' | 'red' | 'gray' => {
  switch (ps) {
    case 'paid': return 'green';
    case 'pending': return 'yellow';
    case 'failed': case 'refunded': return 'red';
    default: return 'gray';
  }
};

export default function OrderHeader({ order, onCancel, onArchive, onLock, onDuplicate, onBack }: OrderHeaderProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const canCancel = order.status === 'pending' || order.status === 'processing';

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-stone-900">
            Order #{order.order_number}
          </h1>
          <p className="text-sm text-stone-500">
            Placed {formatDate(order.created_at)} ({timeAgo(order.created_at)})
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant={paymentBadgeVariant(order.payment_status)}>
          {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
        </Badge>
        <Badge variant={getStatusBadgeVariant(order.status)}>
          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
        </Badge>

        <div className="relative" ref={dropdownRef}>
          <Button variant="ghost" size="sm" onClick={() => setShowDropdown(!showDropdown)}>
            <MoreVertical className="h-4 w-4" />
          </Button>

          {showDropdown && (
            <div className="absolute right-0 top-full mt-1 z-20 w-52 rounded-lg border border-stone-200 bg-white shadow-lg py-1">
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                onClick={() => { onArchive(); setShowDropdown(false); }}
              >
                <Archive className="h-4 w-4" />
                {order.is_archived ? 'Unarchive' : 'Archive'}
              </button>
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                onClick={() => { onLock(); setShowDropdown(false); }}
              >
                {order.is_locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {order.is_locked ? 'Unlock' : 'Lock'}
              </button>
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                onClick={() => { onDuplicate(); setShowDropdown(false); }}
              >
                <Copy className="h-4 w-4" />
                Duplicate as Draft
              </button>
              <div className="my-1 border-t border-stone-100" />
              <a
                href={`${apiUrl}/api/admin/orders/${order.id}/invoice`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                onClick={() => setShowDropdown(false)}
              >
                <FileText className="h-4 w-4" />
                Print Invoice
              </a>
              <a
                href={`${apiUrl}/api/admin/fulfillment/packing-slip/${order.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                onClick={() => setShowDropdown(false)}
              >
                <Printer className="h-4 w-4" />
                Print Packing Slip
              </a>
            </div>
          )}
        </div>

        {canCancel && (
          <Button variant="danger" size="sm" onClick={onCancel}>
            Cancel Order
          </Button>
        )}
      </div>
    </div>
  );
}
